import {Injectable} from '@angular/core';
import {AbstractWidgetService} from '../../services/abstract-widget.service';
import {DocumentEditorActionsType, DocumentInfo, TemplateActionArgs, UiWidget} from '@pt/pt-models';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {GetChapterResponse} from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import {WraValue} from "./v0/models/wraValue";
import {BusinessProjectManagementService, DownloadService, QueryService} from "@pt/api-client";
import {WidgetUtils} from "../widget.utils";
import {WRAValueAnswer} from "./v0/models/wraValueAnswer";
import {ChapterSelector} from "it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector";
import {TreatmentWarning} from "it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning";
import {WRAQueryGlobalMeasureCheckValue} from "./v0/models/widget-risk-assessment-v0-args.dto";
import * as NgrxWidgetActions from '../../store/actions/widget.actions';

import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';

@Injectable({
  providedIn: 'root'
})
export class WidgetRiskAssessmentService extends AbstractWidgetService {

  constructor(public store: Store,
              public downloadService: DownloadService,
              public translateService: TranslateService,
              public service: QueryService,
  ) {
    super(store, translateService);
  }

  /**
   * TRIGGER
   * @param context
   * @param widgets
   * @param widgetId
   * @param action
   * @param args
   * @param queryResponse
   */
  execTrigger(context: GetChapterResponse, widgets: UiWidget[], widgetId: number, action: DocumentEditorActionsType, args: TemplateActionArgs, queryResponse?: any): UiWidget | null {
    const widget = widgets?.find(w => w.internalId === widgetId);
    this.unsupportedTrigger(widget);
    return null;
  }

  /**
   *
   * @param sourceWidget
   * @param targetWidget
   * @param action
   * @param args
   * @param queryResponse
   */
  execAsyncTrigger(sourceWidget: UiWidget, targetWidget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, docInfo: DocumentInfo, chapterSelector: ChapterSelector, queryResponse?: any): void {
    if (targetWidget?.version !== null && targetWidget?.version !== undefined) {
      switch (targetWidget.version) {
        case 0:
          this.execTriggerV2V0(sourceWidget, targetWidget, action, args, docInfo, chapterSelector,);
          break;
        default:
          this.invalidWidgetVersion(sourceWidget);
      }
    }
    this.unsupportedTrigger(sourceWidget);
  }

  private execTriggerV2V0(sourceWidget: UiWidget, targetWidget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, docInfo: DocumentInfo, chapterSelector: ChapterSelector): UiWidget | null {

    switch (action) {
      case DocumentEditorActionsType.ALIGN_CATEGORY:
        this.validWidgetAction(sourceWidget, action);
        return this.alignCategoryAction(sourceWidget, targetWidget, args, docInfo, chapterSelector);

      default:
        this.invalidWidgetAction(sourceWidget, action);
    }
    return null;
  }

  private alignCategoryAction(sourceWidget: UiWidget, targetWidget: UiWidget, args: TemplateActionArgs, docInfo: DocumentInfo, chapterSelector: ChapterSelector) {


    const widget = JSON.parse(JSON.stringify(sourceWidget)) as UiWidget;
    const sourceValue: string[] = widget.value || [];
    //

    this.service.resolve(args.query).subscribe(
      (queryResponse: unknown[]) => {
        const mapping = args.query.mapping;
        const mappedResponse: AsyncTriggerQueryResponse[] = WidgetUtils.mappingQueryResponse(queryResponse, mapping) as AsyncTriggerQueryResponse[];
        const validResponse: AsyncTriggerQueryResponse[] = mappedResponse.filter((r) => sourceValue.includes(r.categoryUUID));
        const targetValue: WraValue = targetWidget.value;
        const targetAnswers: WRAValueAnswer[] = targetValue?.answers || []
        const answers: WRAValueAnswer[] = [];
        validResponse.forEach(r => {
          const targetCurrentAnsware = targetAnswers.find(t => t.uuid === r.uuid);
          if (targetCurrentAnsware) {
            answers.push(targetCurrentAnsware);
          } else {
            answers.push({
              uuid: r.uuid
            })
          }
        })

        // rimuovo il global measure check nel caso non ho più answare
        const requestValue: WraValue = {
          globalMeasureCheckDocId: answers.length > 0 ? targetValue.globalMeasureCheckDocId : undefined,
          answers
        };
        const request: UpsertWidgetsRequest = {
          widgets: [
            {
              internalId: targetWidget?.internalId,
              chapter: chapterSelector,
              version: targetWidget.version,
              type: targetWidget?.type,
              value: requestValue,
              properties: targetWidget?.properties,
              editable: targetWidget?.editable,
              opt: targetWidget?.opt,
              enabled: targetWidget?.enabled
            }]
        };

        const service: BusinessProjectManagementService = this.downloadService.getDocumentEditorTypeService(docInfo.docType);
        if (service) {
          service.upsertWidgets(docInfo.docId, request).subscribe(() => {
            this.store.dispatch(NgrxWidgetActions.disableWarnings({request: {disabledWarnings: [TreatmentWarning.GLOBAL_MEAS_LIST_CHANGED]}}))
            // setTimeout(() => {
            //   this.store.dispatch(NgrxWidgetActions.reloadWidgetAfterSave({widgets: [widget]}));
            // })
          })
        }
      }, () => {
        console.error('[DEV] error ');

      }
    );
    return null;
  }

  /**
   * INITIALIZATION
   * @param context
   * @param widgets
   * @param widgetId
   * @param action
   * @param args
   * @param queryResponse
   */
  initialization(context: GetChapterResponse, widgets: UiWidget[], widgetId: number, action: DocumentEditorActionsType, args: TemplateActionArgs, queryResponse?: any): UiWidget | null {
    const widget = widgets?.find(w => w.internalId === widgetId);
    if (widget?.version !== null && widget?.version !== undefined) {
      switch (widget.version) {
        case 0:
          return this.initializationV0(context, widgets, widget, action, args, queryResponse);
        default:
          this.invalidWidgetVersion(widget);
          return null;
      }
    }
    return null;
  }

  private initializationV0(context: GetChapterResponse, widgets: UiWidget[], widget: UiWidget, action: DocumentEditorActionsType, args: TemplateActionArgs, queryResponse?: any) {
    // this.logInfo(context, widgets, widget?.internalId, action, args, queryResponse);

    let value: WRAQueryGlobalMeasureCheckValue[];
    const currentWidget: UiWidget = JSON.parse(JSON.stringify(widget));
    switch (action) {
      case DocumentEditorActionsType.INITIALIZATION: {
        this.validWidgetAction(widget, action);
        value = WidgetUtils.mappingQueryResponse(queryResponse, args?.query?.mapping) as WRAQueryGlobalMeasureCheckValue[];
        const newAnswers: WRAValueAnswer[] = value.map(v => {
          return {
            uuid: v.uuid
          };
        })
        const newValue: WraValue = {
          answers: newAnswers
        }
        currentWidget.value = newValue;
        return currentWidget;
      }

      default:
        this.invalidWidgetAction(widget, action);
    }
    return null;
  }

}


interface AsyncTriggerQueryResponse {
  uuid: string
  internalId: string
  normativeReference: string
  category: string
  categoryUUID: string;
  description: string
  help: string
  unauthorizedAccess: string
  unauthorizedDisclosure: string
  unauthorizedUpdate: string
  loss: string
  accidentalOrIllegalDestruction: string
  improperUse: string
  checkValue: string
}


